import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Minus, Plus, GitMerge, AlertTriangle } from 'lucide-react';

const DELIVERY_OPTIONS = [{ v: 'M', l: 'Monday' }, { v: 'W', l: 'Wednesday' }, { v: 'NONE', l: 'No delivery day' }];

const groupClients = (line) => {
  const map = {};
  (line?.quantities || []).forEach(q => {
    if (!q.client_name || (q.quantity || 0) <= 0) return;
    map[q.client_name] = (map[q.client_name] || 0) + q.quantity;
  });
  return Object.entries(map).map(([name, qty]) => ({ name, qty })).sort((a, b) => a.name.localeCompare(b.name));
};

const ClientMoveRow = ({ c, keyLabel, value, onChange }) => (
  <tr data-testid={`split-client-row-${c.name}`}>
    <td className="text-sm py-1.5">{c.name}</td>
    <td><span className="badge-pill badge-gray text-[10px]" data-testid={`split-client-key-${c.name}`}>{keyLabel || '—'}</span></td>
    <td className="tabular-nums text-sm text-center">{c.qty}</td>
    <td>
      <div className="flex items-center gap-1 justify-end">
        <button type="button" className="p-1 rounded hover:bg-gray-100 disabled:opacity-30" disabled={value <= 0}
          onClick={() => onChange(value - 1)} data-testid={`split-minus-${c.name}`}><Minus className="w-3 h-3" /></button>
        <Input type="number" min="0" max={c.qty} value={value} className="w-14 h-7 text-center text-sm"
          onChange={(e) => onChange(Math.max(0, Math.min(c.qty, parseInt(e.target.value) || 0)))}
          data-testid={`split-move-${c.name}`} />
        <button type="button" className="p-1 rounded hover:bg-gray-100 disabled:opacity-30" disabled={value >= c.qty}
          onClick={() => onChange(value + 1)} data-testid={`split-plus-${c.name}`}><Plus className="w-3 h-3" /></button>
        <button type="button" className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 hover:bg-purple-100"
          onClick={() => onChange(c.qty)} data-testid={`split-all-${c.name}`}>All</button>
      </div>
    </td>
  </tr>
);

const DaySelects = ({ day, setDay, delivery, setDelivery, days }) => (
  <div className="grid grid-cols-2 gap-3">
    <div className="grid gap-1.5">
      <Label>Move to production day</Label>
      <Select value={day} onValueChange={setDay}>
        <SelectTrigger data-testid="split-day"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="UNASSIGNED">Unassigned</SelectItem>
          {days.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="grid gap-1.5">
      <Label>Delivery day</Label>
      <Select value={delivery} onValueChange={setDelivery}>
        <SelectTrigger data-testid="split-delivery-day"><SelectValue /></SelectTrigger>
        <SelectContent>
          {DELIVERY_OPTIONS.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  </div>
);

export const SplitLineDialog = ({ line, order, days, onClose, onDone }) => {
  const clients = useMemo(() => groupClients(line), [line]);
  const [day, setDay] = useState('UNASSIGNED');
  const [delivery, setDelivery] = useState('NONE');
  const [moves, setMoves] = useState({});
  const [legacyQty, setLegacyQty] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!line) return;
    setDay('UNASSIGNED'); setMoves({}); setSaving(false);
    setDelivery(line.delivery_day || 'NONE');
    setLegacyQty(Math.floor((line.total_quantity || 0) / 2));
  }, [line]);

  const keyOf = useMemo(() => {
    const m = {};
    (order?.client_snapshot || []).forEach(c => { m[c.name] = c.key; });
    return m;
  }, [order]);

  if (!line) return null;
  const hasClients = clients.length > 0;
  const total = hasClients ? clients.reduce((s, c) => s + c.qty, 0) : line.total_quantity;
  const moving = hasClients ? Object.values(moves).reduce((s, v) => s + v, 0) : legacyQty;
  const staying = total - moving;
  const deliveryValue = delivery === 'NONE' ? '' : delivery;
  const sameDay = day === line.production_day && deliveryValue === (line.delivery_day || '');
  const valid = moving >= 1 && staying >= 1 && !sameDay;
  const mergeTarget = hasClients && day !== 'UNASSIGNED' && line.recipe_id && (order?.lines || []).find(l =>
    l.id !== line.id && l.recipe_id === line.recipe_id && (l.meal_size || '') === (line.meal_size || '') &&
    l.production_day === day && (l.delivery_day || '') === deliveryValue && (l.quantities || []).length > 0);

  const submit = async () => {
    setSaving(true);
    try {
      const body = { split_production_day: day, split_delivery_day: deliveryValue };
      if (hasClients) body.allocations = Object.entries(moves).filter(([, q]) => q > 0).map(([client_name, quantity]) => ({ client_name, quantity }));
      else body.split_quantity = legacyQty;
      const res = await axios.post(`${API}/order-lines/${line.id}/split`, body);
      toast.success(`${moving} moved to ${day}${res.data.merged ? ' (added to existing line)' : ''} · ${staying} staying on ${line.production_day}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to split');
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!line} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[560px]" style={{ borderRadius: '12px' }} data-testid="split-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>Split Production</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="font-medium text-sm" data-testid="split-dish-name">
              {line.dish_name}{line.meal_size && <span className="ml-1 badge-pill badge-gray text-[9px]">{line.meal_size}</span>}
            </div>
            <div className="text-xs text-gray-500 mt-1">{total} portions on {line.production_day}</div>
          </div>
          <DaySelects day={day} setDay={setDay} delivery={delivery} setDelivery={setDelivery} days={days} />
          {hasClients ? (
            <div className="max-h-[300px] overflow-y-auto border rounded-lg px-3">
              <table className="w-full" data-testid="split-client-table">
                <thead><tr className="text-[11px] text-gray-500 text-left">
                  <th className="py-2">Client</th><th>Key</th><th className="text-center">Qty</th><th className="text-right">Move</th>
                </tr></thead>
                <tbody>
                  {clients.map(c => (
                    <ClientMoveRow key={c.name} c={c} keyLabel={keyOf[c.name]} value={moves[c.name] || 0}
                      onChange={(v) => setMoves(prev => ({ ...prev, [c.name]: v }))} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="grid gap-2">
              <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 p-2 rounded" data-testid="split-no-clients-warning">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />This line has no client breakdown, so only the number of meals can be split.
              </div>
              <Label>Meals to move</Label>
              <Input type="number" min="1" max={total - 1} value={legacyQty}
                onChange={(e) => setLegacyQty(parseInt(e.target.value) || 0)} data-testid="split-qty" />
            </div>
          )}
          <div className="flex items-center justify-between text-sm p-2.5 rounded-lg bg-purple-50" data-testid="split-summary">
            <span>Staying on {line.production_day}: <b data-testid="split-staying-count">{staying}</b></span>
            <span>Moving to {day}: <b data-testid="split-moving-count">{moving}</b></span>
          </div>
          {mergeTarget && (
            <div className="flex items-center gap-2 text-xs text-teal-700 bg-teal-50 p-2 rounded" data-testid="split-merge-notice">
              <GitMerge className="w-3.5 h-3.5" />Will be added to the existing {day} line ({mergeTarget.total_quantity} portions).
            </div>
          )}
          {sameDay && <p className="text-xs text-amber-700" data-testid="split-same-day-error">Pick a different production or delivery day.</p>}
          {moving > 0 && staying < 1 && <p className="text-xs text-red-600" data-testid="split-validation-error">At least 1 meal must stay on {line.production_day}.</p>}
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="btn-outline" data-testid="cancel-split-btn">Cancel</Button>
          <Button onClick={submit} disabled={!valid || saving} className="btn-primary" data-testid="confirm-split-btn">Split</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
